import every from "lodash/every";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import moment from "moment";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { FIELD_AVAILABILITY_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { usePastDaysLimit } from "PFApp/hooks";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { AVAILABILITY_MODES, AvailabilityInput, DURATION_MODES } from "PFComponents/availability";
import { Label } from "PFComponents/label";
import { utc } from "PFCore/helpers/date";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Availability, AvailabilityMode } from "PFCore/types";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../../activity_form_store";
import { FormSection, SectionProps } from "../../form_section";
import { getAvailabilityErrors } from "./availability_form_section.utils";

type AvailabilityFormSectionProps = SectionProps & { title: string };

export const AvailabilityFormSection = ({ id, title }: AvailabilityFormSectionProps) => {
  const { t } = useTranslation("activities");
  const {
    data: { availability: currentAccountAvailability }
  } = useMyAccountInfo();

  const {
    formState: { errors },
    clearErrors,
    setValue,
    control
  } = useFormContext();

  const pastDaysLimit = usePastDaysLimit();
  const { formatISODate } = useDateFormatter();

  const template = useActivityFormStore((state) => state.template);
  const activity = useActivityFormStore((state) => state.activity);
  const formSchema = useActivityFormStore((state) => state.formSchema);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);

  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const tomorrowISODate = formatISODate(moment().add(1, "days"));
  const required = isFieldRequired({ name: FIELD_AVAILABILITY_KEY });
  const availabilityErrors = getAvailabilityErrors(errors) as Record<string, string>;

  if (!formSchema?.[Sections.Availability]) {
    return null;
  }

  const defaultAvailabilityThreshold = template.schema.properties?.availability?.default_threshold;
  const defaultAvailabilityMode = template.schema.properties?.availability?.default_mode as AvailabilityMode;
  const allowedModes = template.schema.properties?.availability?.allowed_modes || AVAILABILITY_MODES;
  const allowedDurationModes =
    template.schema.definitions.availability_duration?.properties?.mode?.enum || DURATION_MODES;

  const handleAvailabilityChange = (availability: Availability | null, timeRuleModeChecked: boolean) => {
    if (availability) {
      setValue(FIELD_AVAILABILITY_KEY, availability, { shouldDirty: true });
    } else {
      setValue(FIELD_AVAILABILITY_KEY, {}, { shouldDirty: true });
    }
    timeRuleModeChecked !== undefined && clearErrors(FIELD_AVAILABILITY_KEY);
  };

  const minDate = pastDaysLimit
    ? { start: formatISODate(utc().subtract(pastDaysLimit, "days")), end: tomorrowISODate }
    : { start: tomorrowISODate, end: tomorrowISODate };

  return (
    <FormSection id={id} title={<Label text={title} required={required} />}>
      <Controller
        control={control}
        name={FIELD_AVAILABILITY_KEY}
        rules={{
          validate: (value) => {
            const isBlank =
              isEmpty(value) ||
              isEmpty(value.ranges) ||
              every(value.ranges, ({ start, end }) => isNil(start) && isNil(end));
            if (!submittedAsDraft && required && isBlank) {
              return t("form.errors.cantBeBlank");
            }
            return true;
          }
        }}
        render={({ field }) => (
          <AvailabilityInput
            availability={field.value || {}}
            accountAvailability={currentAccountAvailability}
            onChange={handleAvailabilityChange}
            errors={availabilityErrors}
            minDate={minDate}
            allowedModes={allowedModes}
            allowedDurationModes={allowedDurationModes}
            defaultAvailabilityMode={defaultAvailabilityMode}
            defaultAvailabilityThreshold={defaultAvailabilityThreshold}
            activityId={activity.id}
          />
        )}
      />
    </FormSection>
  );
};
